import React, {
  useEffect, useMemo, useCallback, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import ContentHeader from '../../../components/content-header/content-header.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import PageTabs from '../../../components/page-tabs/page-tabs.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsOperationLogUserOperationList
  from './settings-operation-log-user-operation-list/settings-operation-log-user-operation-list.component';
import SettingsOperationLogEmailOperationList
  from './settings-operation-log-email-operation-list/settings-operation-log-email-operation-list.component';
import SettingsOperationLogDealOperationList
  from './settings-operation-log-deal-operation-list/settings-operation-log-deal-operation-list.component';
import SettingsOperationLogApiOperationList
  from './settings-operation-log-api-operation-list/settings-operation-log-api-operation-list.component';
import SettingsOperationLogReportOperationList
  from './settings-operation-log-report-operation-list/settings-operation-log-report-operation-list.component';
import SettingsOperationLogErrorMessageList
  from './settings-operation-log-error-message-list/settings-operation-log-error-message-list.component';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import {
  getOperationLogData, getUserOperationListData, getEmailOperationListData, getDealOperationListData,
  getApiOperationListData, getReportOperationListData, getErrorMessageListData, resetOperationLogData,
  resetOperationLogFileLink,
} from './settings-operation-log.action';
import { getSettingsData } from '../settings.action';
import { ERROR_MESSAGE_LIST_VIEW, SETTINGS_OPERATION_LOG_VIEW } from '../../../constants/entity/permissions';
import { QUERY_PARAM_TAB } from '../../../constants/system/query-params';
import { ROUTE_SETTINGS_OPERATION_LOG } from '../../../constants/system/routes';
import {
  TAB_NAME_AUDIT_LOG, TAB_NAME_API_OPERATIONS, TAB_NAME_DEAL_OPERATIONS, TAB_NAME_EMAIL_OPERATIONS,
  TAB_NAME_USER_OPERATIONS, TAB_NAME_REPORT_OPERATIONS, TAB_NAME_ERROR_MESSAGES,
} from '../../../constants/system/tab-names';

const SettingsOperationLog: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isLoading,
    isDataLoaded,
    isRightDrawerOpen,
    fileLink,
    currentUser,
    userOperationListData,
    userOperationsTotal,
    emailOperationListData,
    emailOperationsTotal,
    dealOperationListData,
    dealOperationsTotal,
    apiOperationListData,
    apiOperationsTotal,
    reportOperationListData,
    reportOperationsTotal,
    errorMessageListData,
    errorMessagesTotal,
    filterUserOperationUserList,
    filterUserOperationTypeList,
    filterEntityTypeList,
    filterEmailOperationUserEmailList,
    filterEmailMessageTypeList,
    filterDealOperationDealList,
    filterDealOperationTypeList,
    filterDealOperationUserList,
    filterApiOperationUserList,
    filterApiEndpointList,
    filterReportOperationUserList,
    filterReportOperationReportList,
  } = props;

  const dispatch: ReduxDispatch = useDispatch();
  const location: ReactRouterLocation = useLocation();
  const navigate: ReactRouterNavigate = useNavigate();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const settingsOperationLogViewPermissions: string[] = useMemo((): string[] => [SETTINGS_OPERATION_LOG_VIEW], []);

  const pageTitle: string = 'Settings: Audit Log';

  const onUserOperationLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getUserOperationListData(options));
  }, []);

  const onEmailOperationLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getEmailOperationListData(options));
  }, []);

  const onDealOperationLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getDealOperationListData(options));
  }, []);

  const onApiOperationLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getApiOperationListData(options));
  }, []);

  const onReportOperationLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getReportOperationListData(options));
  }, []);

  const onErrorMessageLoadBatchData: Function = useCallback((options: RequestOptions): void => {
    dispatch(getErrorMessageListData(options));
  }, []);

  const onUserOperationSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getUserOperationListData(options));
  }, []);

  const onEmailOperationSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getEmailOperationListData(options));
  }, []);

  const onDealOperationSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getDealOperationListData(options));
  }, []);

  const onApiOperationSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getApiOperationListData(options));
  }, []);

  const onReportOperationSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getReportOperationListData(options));
  }, []);

  const onErrorMessageSortParamsChange: Function = useCallback((options: RequestOptions): void => {
    dispatch(getErrorMessageListData(options));
  }, []);

  const onUserOperationApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getUserOperationListData(options));
  }, []);

  const onEmailOperationApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getEmailOperationListData(options));
  }, []);

  const onDealOperationApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getDealOperationListData(options));
  }, []);

  const onApiOperationApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getApiOperationListData(options));
  }, []);

  const onReportOperationApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getReportOperationListData(options));
  }, []);

  const onErrorMessageApplyFilters: Function = useCallback((options: RequestOptions): void => {
    dispatch(getErrorMessageListData(options));
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
    dispatch(getOperationLogData());
  }, []);

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        navigate({
          pathname: ROUTE_SETTINGS_OPERATION_LOG,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams]);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: TAB_NAME_USER_OPERATIONS,
      title: 'User Operations',
      component: (
        <SettingsOperationLogUserOperationList
          isLoading={isLoading}
          userOperationListData={userOperationListData}
          userOperationsTotal={userOperationsTotal}
          filterUserList={filterUserOperationUserList}
          filterUserOperationTypeList={filterUserOperationTypeList}
          filterEntityTypeList={filterEntityTypeList}
          onLoadBatchData={onUserOperationLoadBatchData}
          onSortParamsChange={onUserOperationSortParamsChange}
          onApplyFilters={onUserOperationApplyFilters}
        />
      ),
    },
    {
      id: 2,
      name: TAB_NAME_EMAIL_OPERATIONS,
      title: 'Email Operations',
      component: (
        <SettingsOperationLogEmailOperationList
          isLoading={isLoading}
          emailOperationListData={emailOperationListData}
          emailOperationsTotal={emailOperationsTotal}
          filterUserEmailList={filterEmailOperationUserEmailList}
          filterEmailMessageTypeList={filterEmailMessageTypeList}
          onLoadBatchData={onEmailOperationLoadBatchData}
          onSortParamsChange={onEmailOperationSortParamsChange}
          onApplyFilters={onEmailOperationApplyFilters}
        />
      ),
    },
    {
      id: 3,
      name: TAB_NAME_DEAL_OPERATIONS,
      title: 'Deal Operations',
      component: (
        <SettingsOperationLogDealOperationList
          isLoading={isLoading}
          dealOperationListData={dealOperationListData}
          dealOperationsTotal={dealOperationsTotal}
          filterUserList={filterDealOperationUserList}
          filterDealList={filterDealOperationDealList}
          filterDealOperationTypeList={filterDealOperationTypeList}
          onLoadBatchData={onDealOperationLoadBatchData}
          onSortParamsChange={onDealOperationSortParamsChange}
          onApplyFilters={onDealOperationApplyFilters}
        />
      ),
    },
    {
      id: 4,
      name: TAB_NAME_API_OPERATIONS,
      title: 'API Operations',
      component: (
        <SettingsOperationLogApiOperationList
          isLoading={isLoading}
          apiOperationListData={apiOperationListData}
          apiOperationsTotal={apiOperationsTotal}
          filterUserList={filterApiOperationUserList}
          filterApiEndpointList={filterApiEndpointList}
          onLoadBatchData={onApiOperationLoadBatchData}
          onSortParamsChange={onApiOperationSortParamsChange}
          onApplyFilters={onApiOperationApplyFilters}
        />
      ),
    },
    {
      id: 5,
      name: TAB_NAME_REPORT_OPERATIONS,
      title: 'Report Operations',
      component: (
        <SettingsOperationLogReportOperationList
          isLoading={isLoading}
          reportOperationListData={reportOperationListData}
          reportOperationsTotal={reportOperationsTotal}
          filterUserList={filterReportOperationUserList}
          filterReportList={filterReportOperationReportList}
          onLoadBatchData={onReportOperationLoadBatchData}
          onSortParamsChange={onReportOperationSortParamsChange}
          onApplyFilters={onReportOperationApplyFilters}
        />
      ),
    },
    {
      id: 6,
      name: TAB_NAME_ERROR_MESSAGES,
      title: 'Error Messages',
      permission: ERROR_MESSAGE_LIST_VIEW,
      component: (
        <SettingsOperationLogErrorMessageList
          isLoading={isLoading}
          errorMessageListData={errorMessageListData}
          errorMessagesTotal={errorMessagesTotal}
          onLoadBatchData={onErrorMessageLoadBatchData}
          onSortParamsChange={onErrorMessageSortParamsChange}
          onApplyFilters={onErrorMessageApplyFilters}
        />
      ),
    },
  ];

  useEffect((): ReactUseEffectCleanUp => {
    if (!isDataLoaded) {
      dispatch(getSettingsData());
      dispatch(getOperationLogData());
    }

    return (): void => {
      dispatch(resetOperationLogData());
    };
  }, []);

  useEffect((): void => {
    if (fileLink) {
      ScreenService.downloadFile(fileLink);
      dispatch(resetOperationLogFileLink());
    }
  }, [fileLink]);

  return (
    <CheckAccess permissions={settingsOperationLogViewPermissions}>
      <PullToRefresh onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        <SettingsPageTabs
          isCompressed={isRightDrawerOpen}
          activeTab={TAB_NAME_AUDIT_LOG}
          currentUser={currentUser}
        />
        {
          !isDataLoaded && (
            <Loader isPageLoader withTopOffset />
          )
        }
        {
          isDataLoaded && (
            <PageTabs
              isSubTabs
              isCompressed={isRightDrawerOpen}
              tabs={tabs}
              currentTab={queryTab}
              onTabChange={onTabChange}
            />
          )
        }
      </PullToRefresh>
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    settings,
    settingsOperationLog,
  } = reducers;

  return {
    isDataLoaded: !!settings.settingsData && !!settingsOperationLog.operationLogData,
    isLoading: settingsOperationLog.isLoading,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    fileLink: settingsOperationLog.fileLink,
    currentUser: app.baseData && app.baseData.currentUser,
    userOperationListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.userOperationList,
    userOperationsTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.userOperationsTotal,
    emailOperationListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.emailOperationList,
    emailOperationsTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.emailOperationsTotal,
    dealOperationListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.dealOperationList,
    dealOperationsTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.dealOperationsTotal,
    apiOperationListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.apiOperationList,
    apiOperationsTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.apiOperationsTotal,
    reportOperationListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.reportOperationList,
    reportOperationsTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.reportOperationsTotal,
    errorMessageListData: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.errorMessageList,
    errorMessagesTotal: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.errorMessagesTotal,
    filterUserOperationUserList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.userOperationUserList,
    filterUserOperationTypeList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.userOperationTypeList,
    filterEntityTypeList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.entityTypeList,
    filterEmailOperationUserEmailList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.emailOperationUserEmailList,
    filterEmailMessageTypeList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.emailMessageTypeList,
    filterDealOperationDealList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.dealOperationDealList,
    filterDealOperationTypeList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.dealOperationTypeList,
    filterDealOperationUserList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.dealOperationUserList,
    filterApiOperationUserList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.apiOperationUserList,
    filterApiEndpointList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.apiEndpointList,
    filterReportOperationUserList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.reportOperationUserList,
    filterReportOperationReportList: settingsOperationLog.operationLogData
      && settingsOperationLog.operationLogData.operationLogFilters
      && settingsOperationLog.operationLogData.operationLogFilters.reportOperationReportList,
  };
};

export default connect(mapState)(SettingsOperationLog);
